/**
 * Pestaña "Impuestos". Compara, para cada mes del año seleccionado, lo
 * que el sistema CALCULÓ (ISR + IVA saldo) contra lo que realmente se
 * PAGÓ al SAT (registro manual del usuario). Para cada celda muestra:
 *   - Calculado (snapshot guardado al registrar, fallback al cálculo vivo)
 *   - Pagado
 *   - Diferencia coloreada (ok / pendiente / sobrepago / subpago)
 *   - Línea de captura + fecha de pago
 *
 * Click en una celda → dialog para registrar/editar el pago.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  AlertTriangle,
  Clock,
  X,
  RefreshCw,
} from "lucide-react";
import clsx from "clsx";
import type { TaxFiling } from "@axorial/shared";
import { api } from "@/lib/api";
import { formatMxnCents } from "@/lib/format";

type TaxType = "isr_propio" | "isr_asimilados" | "isr_salarios" | "iva" | "otro";

const TAX_TYPE_LABEL: Record<TaxType, string> = {
  isr_propio: "ISR propio",
  isr_asimilados: "ISR ret. asimilados",
  isr_salarios: "ISR ret. salarios",
  iva: "IVA",
  otro: "Otros",
};

const TAX_TYPE_SHORT: Record<TaxType, string> = {
  isr_propio: "ISR propio",
  isr_asimilados: "ISR asim",
  isr_salarios: "ISR sal",
  iva: "IVA",
  otro: "Otros",
};

const TAX_TYPES: TaxType[] = ["isr_propio", "isr_asimilados", "isr_salarios", "iva", "otro"];

const MES_CORTO = [
  "ene",
  "feb",
  "mar",
  "abr",
  "may",
  "jun",
  "jul",
  "ago",
  "sep",
  "oct",
  "nov",
  "dic",
];

interface MonthSummary {
  isrCents: number;
  ivaSaldoCents: number;
}

export function TaxesTab() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [dialog, setDialog] = useState<{ period: string; taxType: TaxType } | null>(null);
  const qc = useQueryClient();

  const filingsQ = useQuery<{ filings: TaxFiling[] }>({
    queryKey: ["finance", "tax-filings", year],
    queryFn: () => api.get(`/finance/tax-filings?year=${year}`),
  });

  const resync = useMutation({
    mutationFn: () =>
      api.post<{ periodsSynced: number; periods: string[] }>("/finance/tax-filings/resync", {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["finance", "tax-filings"] });
    },
  });

  const filingsByKey = useMemo(() => {
    const m = new Map<string, TaxFiling>();
    for (const f of filingsQ.data?.filings ?? []) {
      m.set(`${f.period}|${f.taxType}`, f);
    }
    return m;
  }, [filingsQ.data]);

  const todayYear = new Date().getFullYear();
  const todayMonth = new Date().getMonth() + 1;

  return (
    <div className="space-y-4">
      <div className="card-elevated p-3 flex items-center gap-2 flex-wrap">
        <button
          type="button"
          onClick={() => setYear(year - 1)}
          className="btn btn-ghost btn-sm btn-square"
          aria-label="Año anterior"
        >
          <ChevronLeft className="size-4" />
        </button>
        <div className="text-lg font-display font-semibold tracking-tight px-2 tabular-nums">
          {year}
        </div>
        <button
          type="button"
          onClick={() => setYear(year + 1)}
          className="btn btn-ghost btn-sm btn-square"
          aria-label="Año siguiente"
        >
          <ChevronRight className="size-4" />
        </button>
        {year !== todayYear && (
          <button type="button" onClick={() => setYear(todayYear)} className="btn btn-ghost btn-xs">
            Hoy
          </button>
        )}
        <span className="ml-auto flex items-center gap-3">
          <button
            type="button"
            onClick={() => resync.mutate()}
            disabled={resync.isPending}
            className="btn btn-ghost btn-xs"
            title="Re-sincronizar tax filings desde las tx marcadas como Pago al SAT con desglose en splits"
          >
            {resync.isPending ? (
              <span className="loading loading-spinner loading-xs" />
            ) : (
              <RefreshCw className="size-3.5" />
            )}
            Sincronizar
          </button>
          <span className="text-xs text-base-content/55">
            Click una celda para registrar el pago al SAT
          </span>
        </span>
      </div>

      <div className="card-elevated overflow-x-auto">
        <table className="table table-sm">
          <thead>
            <tr>
              <th className="text-xs uppercase tracking-wider text-base-content/55">Periodo</th>
              {TAX_TYPES.map((t) => (
                <th
                  key={t}
                  className="text-xs uppercase tracking-wider text-base-content/55 whitespace-nowrap"
                  title={TAX_TYPE_LABEL[t]}
                >
                  {TAX_TYPE_SHORT[t]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: 12 }, (_, i) => i + 1).map((monthNum) => {
              const period = `${year}-${String(monthNum).padStart(2, "0")}`;
              const isFuture = year > todayYear || (year === todayYear && monthNum > todayMonth);
              const isCurrent = year === todayYear && monthNum === todayMonth;
              return (
                <tr
                  key={period}
                  className={clsx(isFuture && "opacity-60", isCurrent && "bg-primary/5")}
                >
                  <td className="font-medium capitalize whitespace-nowrap">
                    {MES_CORTO[monthNum - 1]} {year}
                    {isCurrent && (
                      <span className="ml-2 badge badge-xs badge-primary">en curso</span>
                    )}
                  </td>
                  {TAX_TYPES.map((t) => (
                    <td key={t}>
                      <TaxCell
                        filing={filingsByKey.get(`${period}|${t}`)}
                        period={period}
                        taxType={t}
                        isFuture={isFuture}
                        onClick={() => setDialog({ period, taxType: t })}
                      />
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {dialog && (
        <FilingDialog
          period={dialog.period}
          taxType={dialog.taxType}
          existing={filingsByKey.get(`${dialog.period}|${dialog.taxType}`)}
          onClose={() => setDialog(null)}
        />
      )}
    </div>
  );
}

function TaxCell({
  filing,
  period,
  taxType,
  isFuture,
  onClick,
}: {
  filing: TaxFiling | undefined;
  period: string;
  taxType: TaxType;
  isFuture: boolean;
  onClick: () => void;
}) {
  // Si no hay filing, jalamos el cálculo vivo del summary para mostrar
  // un placeholder. Solo aplica para tipos calculables por el sistema:
  //   - isr_propio = isrCents (RESICO PM 30%)
  //   - iva = ivaSaldoCents (a cargo)
  // Las retenciones de ISR (asimilados/salarios) las captura el usuario.
  const liveQ = useQuery<MonthSummary>({
    enabled: !filing && !isFuture && (taxType === "isr_propio" || taxType === "iva"),
    queryKey: ["finance", "summary", "tax-preview", period],
    queryFn: () => api.get(`/finance/summary?period=${period}&asOf=${period}-28`),
    staleTime: 60_000,
  });

  let calc = filing?.systemCalcCents ?? 0;
  if (!filing && liveQ.data) {
    if (taxType === "isr_propio") calc = liveQ.data.isrCents;
    else if (taxType === "iva") calc = Math.max(0, liveQ.data.ivaSaldoCents);
  }
  const paid = filing?.paidCents ?? 0;
  const diff = paid - calc;

  if (isFuture) {
    return <span className="text-base-content/30">—</span>;
  }
  if (!filing && calc <= 0) {
    return (
      <button
        type="button"
        onClick={onClick}
        className="text-xs text-base-content/40 hover:text-primary"
      >
        Sin movimientos
      </button>
    );
  }

  let status: "ok" | "pending" | "diff" = "pending";
  let StatusIcon: typeof CheckCircle2 = Clock;
  let statusColor = "text-warning";
  if (filing?.paidAt) {
    if (Math.abs(diff) < 100) {
      status = "ok";
      StatusIcon = CheckCircle2;
      statusColor = "text-success";
    } else {
      status = "diff";
      StatusIcon = AlertTriangle;
      statusColor = diff > 0 ? "text-info" : "text-error";
    }
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        "text-left rounded-md px-2 py-1 -ml-2 hover:bg-base-200/60 transition-colors w-full",
      )}
    >
      <div className="flex items-center gap-1.5">
        <StatusIcon className={clsx("size-3.5 shrink-0", statusColor)} />
        <span className="font-mono tabular-nums text-sm">
          {filing ? formatMxnCents(paid) : <span className="text-base-content/55">—</span>}
        </span>
        <span className="text-[10px] text-base-content/40">/ {formatMxnCents(calc)}</span>
      </div>
      {filing?.lineaCaptura && (
        <div className="text-[10px] text-base-content/55 font-mono truncate">
          {filing.lineaCaptura}
        </div>
      )}
      {status === "diff" && filing && (
        <div className={clsx("text-[10px] tabular-nums", statusColor)}>
          {diff > 0 ? `+${formatMxnCents(diff)} sobre` : `${formatMxnCents(diff)} faltan`}
        </div>
      )}
    </button>
  );
}

function FilingDialog({
  period,
  taxType,
  existing,
  onClose,
}: {
  period: string;
  taxType: TaxType;
  existing: TaxFiling | undefined;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const liveQ = useQuery<MonthSummary>({
    enabled: !existing && (taxType === "isr_propio" || taxType === "iva"),
    queryKey: ["finance", "summary", "tax-preview", period],
    queryFn: () => api.get(`/finance/summary?period=${period}&asOf=${period}-28`),
    staleTime: 60_000,
  });

  const systemCalc =
    existing?.systemCalcCents ??
    (liveQ.data
      ? taxType === "isr_propio"
        ? liveQ.data.isrCents
        : taxType === "iva"
          ? Math.max(0, liveQ.data.ivaSaldoCents)
          : 0
      : 0);

  const [paidPesos, setPaidPesos] = useState<string>(
    existing ? (existing.paidCents / 100).toString() : "",
  );
  const [paidAt, setPaidAt] = useState<string>(existing?.paidAt ?? "");
  const [lineaCaptura, setLineaCaptura] = useState<string>(existing?.lineaCaptura ?? "");
  const [notes, setNotes] = useState<string>(existing?.notes ?? "");

  const save = useMutation({
    mutationFn: () =>
      api.post("/finance/tax-filings", {
        period,
        taxType,
        systemCalcCents: systemCalc,
        paidCents: Math.round(parseFloat(paidPesos || "0") * 100),
        paidAt: paidAt || null,
        lineaCaptura: lineaCaptura.trim() || null,
        notes: notes.trim() || null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["finance", "tax-filings"] });
      onClose();
    },
  });

  const del = useMutation({
    mutationFn: () => api.del(`/finance/tax-filings/${existing!.id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["finance", "tax-filings"] });
      onClose();
    },
  });

  const paidCents = Math.round(parseFloat(paidPesos || "0") * 100);
  const diff = paidCents - systemCalc;

  const [y, m] = period.split("-").map(Number);
  const periodLabel = new Date(y!, m! - 1, 1).toLocaleDateString("es-MX", {
    month: "long",
    year: "numeric",
  });

  return (
    <div
      className="modal modal-open"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal-box max-w-lg">
        <div className="flex items-baseline justify-between mb-4">
          <div>
            <div className="text-xs uppercase tracking-wider text-base-content/55">
              {TAX_TYPE_LABEL[taxType]} · Pago al SAT
            </div>
            <h3 className="font-display font-semibold text-lg capitalize">{periodLabel}</h3>
          </div>
          <button type="button" onClick={onClose} className="btn btn-ghost btn-sm btn-square">
            <X className="size-4" />
          </button>
        </div>

        <div className="space-y-3">
          <div className="bg-base-200/50 rounded-md p-3 flex items-center justify-between">
            <span className="text-xs uppercase tracking-wider text-base-content/60 font-semibold">
              Calculado por sistema
            </span>
            <span className="font-mono tabular-nums font-semibold">
              {formatMxnCents(systemCalc)}
            </span>
          </div>

          <label className="form-control">
            <span className="label-text text-xs font-semibold uppercase tracking-wider text-base-content/60">
              Pagado al SAT
            </span>
            <input
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0"
              className="input input-bordered"
              value={paidPesos}
              onChange={(e) => setPaidPesos(e.target.value)}
              placeholder="0.00"
              autoFocus
            />
          </label>

          {paidCents > 0 && Math.abs(diff) >= 100 && (
            <div
              className={clsx(
                "text-xs px-3 py-2 rounded-md",
                diff > 0 ? "bg-info/10 text-info" : "bg-error/10 text-error",
              )}
            >
              {diff > 0 ? (
                <>
                  Pagaste <strong>{formatMxnCents(diff)}</strong> de más sobre lo calculado.
                </>
              ) : (
                <>
                  Faltan <strong>{formatMxnCents(Math.abs(diff))}</strong> respecto a lo calculado.
                </>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <label className="form-control">
              <span className="label-text text-xs font-semibold uppercase tracking-wider text-base-content/60">
                Fecha del pago
              </span>
              <input
                type="date"
                className="input input-bordered input-sm"
                value={paidAt}
                onChange={(e) => setPaidAt(e.target.value)}
              />
            </label>
            <label className="form-control">
              <span className="label-text text-xs font-semibold uppercase tracking-wider text-base-content/60">
                Línea de captura
              </span>
              <input
                type="text"
                className="input input-bordered input-sm uppercase font-mono"
                value={lineaCaptura}
                onChange={(e) => setLineaCaptura(e.target.value)}
                placeholder="0123456789ABCDEF0123"
                maxLength={40}
              />
            </label>
          </div>

          <label className="form-control">
            <span className="label-text text-xs font-semibold uppercase tracking-wider text-base-content/60">
              Notas <span className="text-base-content/40">opcional</span>
            </span>
            <textarea
              className="textarea textarea-bordered text-sm"
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </label>
        </div>

        <div className="modal-action">
          {existing && (
            <button
              type="button"
              onClick={() => {
                if (window.confirm("¿Borrar el registro de este pago?")) del.mutate();
              }}
              disabled={del.isPending}
              className="btn btn-ghost btn-sm text-error mr-auto"
            >
              Borrar
            </button>
          )}
          <button type="button" onClick={onClose} className="btn btn-ghost btn-sm">
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => save.mutate()}
            disabled={save.isPending}
            className="btn btn-primary btn-sm"
          >
            {save.isPending ? <span className="loading loading-spinner loading-xs" /> : null}
            Guardar
          </button>
        </div>
      </div>
    </div>
  );
}
